import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please enter your email and password');
      return;
    }

    // Simple auth check (localStorage for now)
    const existingProfile = localStorage.getItem('user_profile');
    
    if (existingProfile) {
      const profile = JSON.parse(existingProfile);
      
      // Simple validation (in production, this would be backend)
      if (profile.email === formData.email) {
        localStorage.setItem('is_authenticated', 'true');
        
        const displayName = profile.name || 'love';
        toast.success(`Welcome back, ${displayName}. Your space is safe and waiting for you. 💜`);
        navigate('/');
        return;
      }
    }
    
    toast.error('Account not found. Would you like to create a new space?');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E2536] via-[#3E324D] to-[#2E2536] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Heart className="w-8 h-8 text-[#E7C6C2]" fill="#E7C6C2" />
            <h1 className="text-4xl font-bold text-[#EDE8F3]">Welcome Back</h1>
          </div>
          <p className="text-[#C8B5E0] text-lg">
            Your space is safe and waiting for you
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#3E324D] rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[#C8B5E0] text-sm mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#C8B5E0]" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full bg-[#2E2536] text-[#EDE8F3] pl-11 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E7C6C2]"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-[#C8B5E0] text-sm mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#C8B5E0]" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full bg-[#2E2536] text-[#EDE8F3] pl-11 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E7C6C2]"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] py-3 rounded-full font-semibold"
            >
              Return to Your Space
            </Button>
          </form>

          {/* Signup Link */}
          <div className="mt-6 text-center">
            <p className="text-[#C8B5E0] text-sm">
              New here?{' '}
              <button
                onClick={() => navigate('/signup')}
                className="text-[#E7C6C2] hover:text-[#F7E5E5] font-medium"
              >
                Create your safe space
              </button>
            </p>
          </div>

          {/* Forgot Password */}
          <div className="mt-4 text-center">
            <button className="text-[#C8B5E0] text-xs hover:text-[#EDE8F3]">
              Forgot password?
            </button>
          </div>
        </div>

        {/* Privacy Note */}
        <p className="text-center text-[#C8B5E0] text-xs mt-6 max-w-sm mx-auto">
          Your privacy and safety are our priority.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
